/**
 * Date-only helpers for calendar values such as a birthdate.
 *
 * A birthdate is a calendar date, not an instant in time, so it must never be
 * put through a timezone conversion. The common `date.toISOString().split("T")[0]`
 * shortcut is wrong here: it converts the local Date to UTC first, so a date
 * picked at local midnight in a positive-offset timezone (e.g. 2015-07-01 at
 * UTC+3) serializes to the previous calendar day (2015-06-30). These helpers
 * read/write the LOCAL calendar components instead, so the day the user picked
 * round-trips unchanged in every timezone.
 */

/** Format a Date as `YYYY-MM-DD` from its local calendar components. */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse a `YYYY-MM-DD` (or leading-`YYYY-MM-DD` of an ISO string) into a Date at
 * LOCAL midnight, so the calendar day is preserved. Returns null for anything
 * that isn't a parseable date-only string.
 */
export function parseDateOnly(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
}
